import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

import { logger } from './../../../utils/utils';

const useStyles = makeStyles((theme) => ({
  popper: {
    zIndex: theme.zIndex.drawer + 1,
  },
  circularProgress: {
    color: '#EFEFEF',
    marginRight: 4,
    marginLeft: -4,
  },
  color: {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: 3,
    marginRight: 8,
    marginTop: 2,
  },
}));

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

/**
 * ButtonGroupSelectOptions
 * @param {object} prop
 * @param {string} value ,current value
 * @param {string} options ,options of ButtonGroup
 * @param {function} hiddenOption, hide or show option in  bizOptionsBuz
 * @param {function} disabledOption
 * @param {function} onChange , callback fucntion after group is changed
 * @returns
 * Note: Adapt from https://material-ui.com/components/button-group/ (Split button)
 */
const ButtonGroupSelectOptions = forwardRef(
  (
    { value = '', options = [], hiddenOption, disabledOption, onChange },
    ref
  ) => {
    const classes = useStyles();
    logger('ButtonGroupSelectOptions render');

    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef(null);

    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(-1);

    const prevSelectedIndexRef = useRef(-1);

    useImperativeHandle(ref, () => ({
      undoSelected() {
        setSelectedIndex(prevSelectedIndexRef.current);
      },
    }));

    //Effect, Customize selectedIndex when have value prop incomming
    useEffect(() => {
      if (value) {
        let initialSelectIndex = options.findIndex((item) => item.id === value);
        setSelectedIndex(initialSelectIndex);
        prevSelectedIndexRef.current = initialSelectIndex;
      }
    }, [value]);

    let style = selectedIndex > -1 ? options[selectedIndex].style : {};

    const handleClick = () => {
      // console.info(`You clicked ${options[selectedIndex]}`);
    };

    const handleMenuItemClick = async (option, event, index) => {
      //Original & It's necessary
      setOpen(false);

      //Original & It's necessary
      setSelectedIndex(index);

      // Prop for enhance ButtonGroupStatus
      if (onChange) {
        !loading && setLoading(true);
        await onChange({
          id: option.id,
          name: option.name,
          description: option.description,
        });
        setLoading(false);
      }
    };

    const handleToggle = () => {
      setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
      if (anchorRef.current && anchorRef.current.contains(event.target)) {
        return;
      }

      setOpen(false);
    };

    return (
      <Grid container direction="column" alignItems="flex-start">
        {selectedIndex == -1 && (
          <HtmlTooltip
            title={
              <React.Fragment>
                <Typography color="error">
                  <Box fontSize={14}>สถานะของการดำเนินการผิดพลาด</Box>
                </Typography>
                <p>
                  เนื่องจากสถานะของเหตุละเมิดไม่มีอยู่ในรายการตัวเลือกสถานะนี้
                </p>{' '}
                <p>กรุณาแจ้งผู้ดูแลระบบฯ</p>
              </React.Fragment>
            }
          >
            <h4 style={{ color: 'red' }}>สถานะผิดพลาด</h4>
          </HtmlTooltip>
        )}
        {selectedIndex > -1 && (
          <Grid item xs={12}>
            <ButtonGroup
              variant="text"
              size="small"
              ref={anchorRef}
              aria-label="split button"
              disabled={loading}
            >
              <Button
                variant="outlined"
                onClick={handleClick}
                style={{
                  minWidth: 120,
                  ...JSON.parse(style),
                }}
                disabled={loading}
              >
                {loading && (
                  <CircularProgress
                    size={20}
                    className={classes.circularProgress}
                  />
                )}{' '}
                {options[selectedIndex].name}
              </Button>
              <Button
                variant="outlined"
                aria-controls={open ? 'split-button-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-label="select merge strategy"
                aria-haspopup="menu"
                onClick={handleToggle}
                style={{ ...JSON.parse(style) }}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              disablePortal
              className={classes.popper}
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === 'bottom' ? 'center top' : 'center bottom',
                  }}
                >
                  <Paper className={classes.paper}>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList
                        id="split-button-menu"
                        style={{ minWidth: 160, border: '1px solid #999' }}
                      >
                        {options.map(
                          (option, index) =>
                            index !== selectedIndex && (
                              <div
                                key={index}
                                hidden={
                                  hiddenOption ? hiddenOption(option) : false
                                }
                              >
                                <MenuItem
                                  key={option.id}
                                  disabled={
                                    disabledOption
                                      ? disabledOption(option)
                                      : false
                                  }
                                  selected={index === selectedIndex}
                                  onClick={(event) =>
                                    handleMenuItemClick(option, event, index)
                                  }
                                >
                                  <span
                                    className={classes.color}
                                    style={
                                      option.style
                                        ? JSON.parse(option.style)
                                        : ''
                                    }
                                  />
                                  {option.name}
                                </MenuItem>
                              </div>
                            )
                        )}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Grid>
        )}
      </Grid>
    );
  }
);

ButtonGroupSelectOptions.propTypes = {};

export default React.memo(ButtonGroupSelectOptions);
